import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  SimpleGrid,
  HStack,
  VStack,
  useToast,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ChevronDownIcon, CheckIcon } from "@chakra-ui/icons";
import axios from "axios";
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  query as firestoreQuery,
} from "firebase/firestore";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import AppPhoneInput, {
  COUNTRY_OPTIONS,
  buildInternationalNumber,
  splitPhoneNumber,
} from "../Shared/PhoneInput";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const FIRESTORE_USERS_COLLECTION = "users";
const FIRESTORE_USER_LIMIT = 500;

type SearchableDriverUser = {
  id: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
};

type DriverAvailability = {
  online: boolean;
  eligible: boolean;
  reason: string;
  currentJobs: number;
  maxConcurrentJobs: number;
};

const getUserId = (value: any) =>
  String(
    value?.firestoreDocId ||
      value?.id ||
      value?.uid ||
      value?.firebaseUid ||
      value?.userId ||
      value?._id ||
      "",
  ).trim();

const getUserPhoneNumber = (value: any) =>
  String(
    value?.phoneNumber ||
      value?.phone ||
      value?.mobileNumber ||
      value?.mobile ||
      value?.cellNumber ||
      value?.cellphone ||
      "",
  ).trim();

const getUserWhatsAppNumber = (value: any) =>
  String(
    value?.whatsappNumber ||
      value?.whatsAppNumber ||
      value?.whatsapp ||
      value?.whatsApp ||
      getUserPhoneNumber(value) ||
      "",
  ).trim();

const toSearchableDriverUser = (value: any): SearchableDriverUser | null => {
  const id = getUserId(value);

  if (!id) {
    return null;
  }

  const email = String(value?.email || "").trim();

  const name = String(
    value?.displayName ||
      value?.fullName ||
      value?.name ||
      value?.businessName ||
      email ||
      id,
  ).trim();

  const phoneNumber = getUserPhoneNumber(value);
  const whatsappNumber = getUserWhatsAppNumber(value) || phoneNumber;

  return {
    id,
    name: name || id,
    email: email || undefined,
    phoneNumber: phoneNumber || undefined,
    whatsappNumber: whatsappNumber || phoneNumber || undefined,
  };
};

const emptyForm = {
  name: "",
  phoneCountryCode: "MW",
  phoneNumber: "",
  whatsappCountryCode: "MW",
  whatsappNumber: "",
  vehicleType: "motorbike",
  status: "active",
  currentShiftStatus: "off_shift",
};

const vehicleOptions = [
  { value: "motorbike", label: "Motorbike" },
  { value: "bicycle", label: "Bicycle" },
  { value: "car", label: "Car" },
  { value: "van", label: "Van" },
  { value: "truck", label: "Truck" },
  { value: "other", label: "Other" },
];

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "offline", label: "Offline" },
  { value: "paused", label: "Paused" },
  { value: "suspended", label: "Suspended" },
];

const shiftOptions = [
  { value: "on_shift", label: "On Shift" },
  { value: "off_shift", label: "Off Shift" },
  { value: "break", label: "Break" },
];

const getDriverAvailability = (driver: any): DriverAvailability => {
  const status = String(driver?.status || "").toLowerCase();

  const shiftStatus = String(driver?.currentShiftStatus || "").toLowerCase();

  const currentJobs = Math.max(0, Number(driver?.currentJobsCount || 0));

  const maxConcurrentJobs = Math.max(0, Number(driver?.maxConcurrentJobs ?? 2));

  const serviceCategories = Array.isArray(driver?.serviceCategories)
    ? driver.serviceCategories.map((value: any) =>
        String(value).trim().toLowerCase(),
      )
    : [];

  const explicitOnline =
    typeof driver?.online === "boolean"
      ? driver.online
      : typeof driver?.isOnline === "boolean"
        ? driver.isOnline
        : undefined;

  /*
   * Existing driver quick action treats status=active as Online,
   * therefore we keep that exact meaning here.
   */
  const online =
    explicitOnline !== undefined ? explicitOnline : status === "active";

  const onShift = shiftStatus === "on_shift";

  const supportsDispatch =
    serviceCategories.includes("delivery") ||
    serviceCategories.includes("product_order");

  const hasCapacity = maxConcurrentJobs > 0 && currentJobs < maxConcurrentJobs;

  const calculatedEligible =
    online && onShift && supportsDispatch && hasCapacity;

  const explicitEligible =
    typeof driver?.eligible === "boolean"
      ? driver.eligible
      : typeof driver?.isEligible === "boolean"
        ? driver.isEligible
        : undefined;

  const eligible =
    explicitEligible !== undefined ? explicitEligible : calculatedEligible;

  let reason = "Ready for jobs";

  if (!online) {
    reason = "Driver is offline";
  } else if (!onShift) {
    reason = "Driver is off shift";
  } else if (!supportsDispatch) {
    reason = "Delivery service not enabled";
  } else if (!hasCapacity) {
    reason = `At capacity (${currentJobs}/${maxConcurrentJobs})`;
  }

  if (explicitEligible === false && driver?.eligibilityReason) {
    reason = String(driver.eligibilityReason);
  }

  return {
    online,
    eligible,
    reason,
    currentJobs,
    maxConcurrentJobs,
  };
};

type StyledMenuSelectProps = {
  label: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  onChange: (value: string) => void;
  theme: any;
};

const StyledMenuSelect: React.FC<StyledMenuSelectProps> = ({
  label,
  value,
  options,
  onChange,
  theme,
}) => {
  const selected =
    options.find((option) => option.value === value) || options[0];

  return (
    <Box>
      <Text mb={2} fontSize="sm" fontWeight="600" color={theme.text}>
        {label}
      </Text>

      <Menu placement="bottom-start">
        <MenuButton
          as={Button}
          w="100%"
          h="40px"
          px={4}
          rightIcon={<ChevronDownIcon />}
          border={`1px solid ${theme.border}`}
          borderRadius="12px"
          bg={theme.surface}
          color={theme.text}
          fontSize="sm"
          fontWeight="600"
          textAlign="left"
          _hover={{
            bg: theme.cardBg || theme.background,
          }}
          _active={{
            bg: theme.cardBg || theme.background,
          }}
          _focus={{
            borderColor: theme.primary || "#3182ce",
            boxShadow: `0 0 0 1px ${theme.primary || "#3182ce"}`,
          }}
        >
          <Text isTruncated textAlign="left">
            {selected.label}
          </Text>
        </MenuButton>

        <MenuList
          p={2}
          minW="240px"
          borderRadius="14px"
          border={`1px solid ${theme.border}`}
          bg={theme.surface}
          boxShadow={theme.shadowSm}
          zIndex={20}
        >
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <MenuItem
                key={option.value}
                onClick={() => onChange(option.value)}
                borderRadius="10px"
                px={3}
                py={3}
                mb={1}
                bg={
                  isSelected ? theme.cardBg || theme.background : "transparent"
                }
                _hover={{
                  bg: theme.cardBg || theme.background,
                }}
                _focus={{
                  bg: theme.cardBg || theme.background,
                }}
              >
                <HStack justify="space-between" w="full" spacing={3}>
                  <Text
                    fontSize="sm"
                    fontWeight={isSelected ? "800" : "600"}
                    color={theme.text}
                  >
                    {option.label}
                  </Text>

                  {isSelected && (
                    <CheckIcon
                      boxSize={3}
                      color={theme.primary || "blue.500"}
                    />
                  )}
                </HStack>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Box>
  );
};

const DriversPage: React.FC = () => {
  const { theme } = useCustomTheme();

  const toast = useToast();

  const [loading, setLoading] = useState(true);

  const [rows, setRows] = useState<any[]>([]);

  const [form, setForm] = useState(emptyForm);

  const [editingDriverId, setEditingDriverId] = useState<string | null>(null);

  const [linkedUserId, setLinkedUserId] = useState("");

  const [linkedUserName, setLinkedUserName] = useState("");

  const [userSearch, setUserSearch] = useState("");

  const [userSearchResults, setUserSearchResults] = useState<
    SearchableDriverUser[]
  >([]);

  const [searchingUsers, setSearchingUsers] = useState(false);

  const [userSearchOpen, setUserSearchOpen] = useState(false);

  const [firestoreUsers, setFirestoreUsers] = useState<SearchableDriverUser[]>(
    [],
  );

  const [firestoreUsersLoaded, setFirestoreUsersLoaded] = useState(false);

  const selectedPhoneCountry = useMemo(
    () =>
      COUNTRY_OPTIONS.find((item) => item.code === form.phoneCountryCode) ||
      COUNTRY_OPTIONS[0],
    [form.phoneCountryCode],
  );

  const selectedWhatsAppCountry = useMemo(
    () =>
      COUNTRY_OPTIONS.find((item) => item.code === form.whatsappCountryCode) ||
      COUNTRY_OPTIONS[0],
    [form.whatsappCountryCode],
  );

  /*
   * Firestore user search.
   *
   * We load the user directory once and then filter
   * locally while the administrator is typing.
   *
   * This ensures the selected phone information is
   * the actual data stored on the Firestore user
   * profile instead of a separate driver-directory
   * representation.
   */
  useEffect(() => {
    const searchText = userSearch.trim().toLowerCase();

    if (searchText.length < 2) {
      setUserSearchResults([]);
      setUserSearchOpen(false);
      setSearchingUsers(false);
      return;
    }

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setSearchingUsers(true);

      try {
        let users = firestoreUsers;

        if (!firestoreUsersLoaded) {
          const db = getFirestore();

          const usersQuery = firestoreQuery(
            collection(db, FIRESTORE_USERS_COLLECTION),
            limit(FIRESTORE_USER_LIMIT),
          );

          const snapshot = await getDocs(usersQuery);

          users = snapshot.docs
            .map((document) =>
              toSearchableDriverUser({
                ...document.data(),
                firestoreDocId: document.id,
              }),
            )
            .filter(Boolean) as SearchableDriverUser[];

          if (!cancelled) {
            setFirestoreUsers(users);

            setFirestoreUsersLoaded(true);
          }
        }

        const results = users
          .filter((user) => {
            const searchableValues = [
              user.id,
              user.name,
              user.email,
              user.phoneNumber,
              user.whatsappNumber,
            ]
              .filter(Boolean)
              .map((value) => String(value).toLowerCase());

            return searchableValues.some((value) => value.includes(searchText));
          })
          .slice(0, 25);

        if (!cancelled) {
          setUserSearchResults(results);

          setUserSearchOpen(true);
        }
      } catch (error: any) {
        if (cancelled) {
          return;
        }

        console.error("Failed to search Firestore users for driver:", error);

        setUserSearchResults([]);

        setUserSearchOpen(true);

        toast({
          title: "Failed to search users",
          description:
            error?.message || "Unable to read user profiles from Firestore.",
          status: "error",
        });
      } finally {
        if (!cancelled) {
          setSearchingUsers(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [userSearch, firestoreUsers, firestoreUsersLoaded, toast]);

  const selectDriverUser = (selectedUser: SearchableDriverUser) => {
    /*
     * Firestore phoneNumber is the primary cell number.
     *
     * For WhatsApp:
     * - use explicit Firestore whatsappNumber when present
     * - otherwise use Firestore phoneNumber
     */
    const rawCellNumber =
      selectedUser.phoneNumber || selectedUser.whatsappNumber || "";

    const rawWhatsAppNumber =
      selectedUser.whatsappNumber || selectedUser.phoneNumber || "";

    const cell = splitPhoneNumber(rawCellNumber);

    const whatsapp = splitPhoneNumber(rawWhatsAppNumber);

    setLinkedUserId(selectedUser.id);

    setLinkedUserName(selectedUser.name);

    setForm((current) => ({
      ...current,

      name: selectedUser.name || current.name,

      phoneCountryCode: rawCellNumber
        ? cell.countryCode
        : current.phoneCountryCode,

      phoneNumber: rawCellNumber ? cell.localNumber : current.phoneNumber,

      whatsappCountryCode: rawWhatsAppNumber
        ? whatsapp.countryCode
        : current.whatsappCountryCode,

      whatsappNumber: rawWhatsAppNumber
        ? whatsapp.localNumber
        : current.whatsappNumber,

      /*
       * Newly linked drivers continue to start
       * off-shift, exactly as before.
       */
      currentShiftStatus: "off_shift",
    }));

    setUserSearch("");
    setUserSearchResults([]);
    setUserSearchOpen(false);
  };

  const load = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/api/v1/drivers`);

      setRows(res.data?.data || []);
    } catch (error) {
      console.error(error);

      toast({
        title: "Failed to load drivers",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingDriverId(null);
    setLinkedUserId("");
    setLinkedUserName("");
    setUserSearch("");
    setUserSearchResults([]);
    setUserSearchOpen(false);
  };

  const saveDriver = async () => {
    try {
      const fullPhoneNumber = buildInternationalNumber(
        form.phoneCountryCode,
        form.phoneNumber,
      );

      const fullWhatsAppNumber = buildInternationalNumber(
        form.whatsappCountryCode,
        form.whatsappNumber,
      );

      if (!form.name || !fullPhoneNumber || !fullWhatsAppNumber) {
        toast({
          title: "Name, phone number and WhatsApp number are required",
          status: "warning",
        });

        return;
      }

      const payload = {
        name: form.name,

        phoneNumber: fullPhoneNumber,

        whatsappNumber: fullWhatsAppNumber,

        vehicleType: form.vehicleType,

        status: form.status,

        currentShiftStatus: form.currentShiftStatus || "off_shift",

        ...(linkedUserId
          ? {
              userId: linkedUserId,
            }
          : {}),
      };

      if (editingDriverId) {
        await axios.patch(
          `${API_BASE}/api/v1/drivers/${editingDriverId}`,
          payload,
        );

        toast({
          title: "Driver updated",
          status: "success",
        });
      } else {
        await axios.post(`${API_BASE}/api/v1/drivers`, payload);

        toast({
          title: "Driver created",
          status: "success",
        });
      }

      resetForm();

      await load();
    } catch (error) {
      console.error(error);

      toast({
        title: "Failed to save driver",
        status: "error",
      });
    }
  };

  const startEdit = (row: any) => {
    const splitPhone = splitPhoneNumber(row.phoneNumber || "");

    const splitWhatsApp = splitPhoneNumber(
      row.whatsappNumber || row.phoneNumber || "",
    );

    setEditingDriverId(row.id);

    const userId = String(
      row.userId ||
        row.firebaseUid ||
        row.accountUserId ||
        row.ownerUserId ||
        "",
    ).trim();

    setLinkedUserId(userId);

    setLinkedUserName(row.linkedUserName || row.name || "");

    setUserSearch("");
    setUserSearchResults([]);
    setUserSearchOpen(false);

    setForm({
      name: row.name || "",

      phoneCountryCode: splitPhone.countryCode,

      phoneNumber: splitPhone.localNumber,

      whatsappCountryCode: splitWhatsApp.countryCode,

      whatsappNumber: splitWhatsApp.localNumber,

      vehicleType: row.vehicleType || "motorbike",

      status: row.status || "active",

      currentShiftStatus: row.currentShiftStatus || "off_shift",
    });
  };

  const removeDriver = async (driverId: string) => {
    try {
      await axios.delete(`${API_BASE}/api/v1/drivers/${driverId}`);

      toast({
        title: "Driver deleted",
        status: "success",
      });

      if (editingDriverId === driverId) {
        resetForm();
      }

      await load();
    } catch (error) {
      console.error(error);

      toast({
        title: "Failed to delete driver",
        status: "error",
      });
    }
  };

  const updateDriverQuick = async (
    driverId: string,
    updates: Record<string, unknown>,
  ) => {
    try {
      await axios.patch(`${API_BASE}/api/v1/drivers/${driverId}`, updates);

      await load();
    } catch (error) {
      console.error(error);

      toast({
        title: "Failed to update driver",
        status: "error",
      });
    }
  };

  return (
    <Box
      p={{
        base: 0,
        md: 2,
      }}
      bg={theme.background}
      minH="100%"
      minW={0}
    >
      <Text fontSize="2xl" fontWeight="800" color={theme.text} mb={6}>
        Drivers
      </Text>

      <Box
        p={{
          base: 3,
          md: 5,
        }}
        borderRadius="20px"
        bg={theme.surface}
        border={`1px solid ${theme.border}`}
        boxShadow={theme.shadowSm}
        mb={6}
      >
        <HStack justify="space-between" align="start" mb={4} flexWrap="wrap">
          <Box>
            <Text fontWeight="700" color={theme.text} fontSize="lg">
              {editingDriverId ? "Edit Driver" : "Add Driver"}
            </Text>

            <Text color={theme.text} fontSize="sm">
              Add full driver contact details with country-aware numbers.
            </Text>
          </Box>

          {editingDriverId && (
            <Badge colorScheme="orange" px={3} py={1} borderRadius="full">
              Editing
            </Badge>
          )}
        </HStack>

        <SimpleGrid
          columns={{
            base: 1,
            md: 2,
            xl: 3,
          }}
          spacing={4}
        >
          {!editingDriverId && (
            <Box
              gridColumn={{
                base: "auto",
                md: "1 / -1",
              }}
              position="relative"
            >
              <Text mb={2} fontSize="sm" fontWeight="600" color={theme.text}>
                Search existing user
              </Text>

              <Input
                h="40px"
                value={userSearch}
                onChange={(event) => {
                  setUserSearch(event.target.value);

                  /*
                   * Typing a new search means
                   * the admin is choosing another
                   * Firestore user.
                   */
                  if (linkedUserId) {
                    setLinkedUserId("");

                    setLinkedUserName("");
                  }
                }}
                onFocus={() => {
                  if (userSearchResults.length > 0) {
                    setUserSearchOpen(true);
                  }
                }}
                placeholder="Search Firestore users by name, email, phone or user ID"
                bg={theme.inputBg}
                color={theme.text}
                borderColor={theme.inputBorder}
                _placeholder={{
                  color: theme.inputPlaceholder,
                }}
                _hover={{
                  borderColor: theme.inputBorderFocus,
                }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />

              {searchingUsers && (
                <Spinner
                  size="sm"
                  position="absolute"
                  right="12px"
                  top="39px"
                  color={theme.primary}
                />
              )}

              {userSearchOpen && (
                <VStack
                  position="absolute"
                  top="calc(100% + 6px)"
                  left={0}
                  right={0}
                  zIndex={50}
                  spacing={0}
                  align="stretch"
                  maxH="300px"
                  overflowY="auto"
                  bg={theme.surface}
                  border={`1px solid ${theme.border}`}
                  borderRadius="12px"
                  boxShadow={theme.shadowMd || theme.shadowSm}
                  p={1}
                >
                  {userSearchResults.length === 0 ? (
                    <Text p={3} fontSize="sm" color={theme.textMuted}>
                      No matching Firestore users found.
                    </Text>
                  ) : (
                    userSearchResults.map((candidate) => (
                      <Box
                        key={candidate.id}
                        px={3}
                        py={2}
                        borderRadius="9px"
                        cursor="pointer"
                        _hover={{
                          bg: theme.hover,
                        }}
                        onClick={() => selectDriverUser(candidate)}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          color={theme.text}
                          noOfLines={1}
                        >
                          {candidate.name}
                        </Text>

                        <Text
                          fontSize="xs"
                          color={theme.textMuted}
                          noOfLines={1}
                        >
                          {candidate.email || candidate.id}
                        </Text>

                        <Text
                          mt={1}
                          fontSize="xs"
                          color={theme.textSubtle}
                          noOfLines={1}
                        >
                          {candidate.phoneNumber
                            ? `Cell: ${candidate.phoneNumber}`
                            : "No cell number"}

                          {" • "}

                          {candidate.whatsappNumber
                            ? `WhatsApp: ${candidate.whatsappNumber}`
                            : candidate.phoneNumber
                              ? `WhatsApp: ${candidate.phoneNumber}`
                              : "No WhatsApp number"}
                        </Text>
                      </Box>
                    ))
                  )}
                </VStack>
              )}

              {linkedUserId ? (
                <HStack mt={2} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="green" borderRadius="6px" px={2} py={1}>
                    Firestore user linked
                  </Badge>

                  <Text fontSize="xs" color={theme.textMuted}>
                    {linkedUserName || linkedUserId}
                  </Text>
                </HStack>
              ) : (
                <Text mt={1} fontSize="xs" color={theme.textMuted}>
                  Search user profiles directly from Firestore. Selecting a user
                  prefills their name, cell number and WhatsApp number.
                </Text>
              )}
            </Box>
          )}

          <Box>
            <Text mb={2} fontSize="sm" fontWeight="600" color={theme.text}>
              Driver name
            </Text>

            <Input
              h="40px"
              placeholder="Driver name"
              value={form.name}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  name: e.target.value,
                }))
              }
            />
          </Box>

          <AppPhoneInput
            label="Cell number"
            value={form.phoneNumber}
            countryCode={form.phoneCountryCode}
            placeholder={`Enter ${selectedPhoneCountry.label} cell number`}
            onCountryChange={(value) =>
              setForm((p) => ({
                ...p,
                phoneCountryCode: value,
              }))
            }
            onNumberChange={(value) =>
              setForm((p) => ({
                ...p,
                phoneNumber: value,
              }))
            }
            theme={theme}
          />

          <AppPhoneInput
            label="WhatsApp number"
            value={form.whatsappNumber}
            countryCode={form.whatsappCountryCode}
            placeholder={`Enter ${selectedWhatsAppCountry.label} WhatsApp`}
            onCountryChange={(value) =>
              setForm((p) => ({
                ...p,
                whatsappCountryCode: value,
              }))
            }
            onNumberChange={(value) =>
              setForm((p) => ({
                ...p,
                whatsappNumber: value,
              }))
            }
            theme={theme}
          />

          <StyledMenuSelect
            label="Vehicle type"
            value={form.vehicleType}
            options={vehicleOptions}
            onChange={(value) =>
              setForm((p) => ({
                ...p,
                vehicleType: value,
              }))
            }
            theme={theme}
          />

          <StyledMenuSelect
            label="Availability status"
            value={form.status}
            options={statusOptions}
            onChange={(value) =>
              setForm((p) => ({
                ...p,
                status: value,
              }))
            }
            theme={theme}
          />

          <StyledMenuSelect
            label="Shift status"
            value={form.currentShiftStatus}
            options={shiftOptions}
            onChange={(value) =>
              setForm((p) => ({
                ...p,
                currentShiftStatus: value,
              }))
            }
            theme={theme}
          />
        </SimpleGrid>

        <Divider my={5} />

        <HStack
          spacing={3}
          flexWrap="wrap"
          align="stretch"
          sx={{
            "& > button": {
              flex: {
                base: "1 1 100%",
                sm: "0 0 auto",
              },
            },
          }}
        >
          <Button
            onClick={saveDriver}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{
              bgImage: theme.gradients.primaryButtonHover,
            }}
          >
            {editingDriverId ? "Update Driver" : "Create Driver"}
          </Button>

          {editingDriverId && (
            <Button variant="outline" onClick={resetForm}>
              Cancel Edit
            </Button>
          )}
        </HStack>
      </Box>

      <Box
        p={{
          base: 3,
          md: 5,
        }}
        borderRadius="20px"
        bg={theme.surface}
        border={`1px solid ${theme.border}`}
        boxShadow={theme.shadowSm}
      >
        <HStack
          justify="space-between"
          align="center"
          mb={4}
          spacing={3}
          flexWrap="wrap"
        >
          <Box>
            <Text fontWeight="700" color={theme.text} fontSize="lg">
              Driver List
            </Text>

            {!loading && rows.length > 0 && (
              <Text fontSize="xs" color={theme.textMuted}>
                {rows.length} {rows.length === 1 ? "driver" : "drivers"}
              </Text>
            )}
          </Box>

          {!loading && rows.length > 0 && (
            <HStack spacing={2}>
              <Badge colorScheme="green" borderRadius="6px" px={2} py={1}>
                {rows.filter((row) => getDriverAvailability(row).online).length}{" "}
                Online
              </Badge>

              <Badge
                colorScheme="green"
                variant="outline"
                borderRadius="6px"
                px={2}
                py={1}
              >
                {
                  rows.filter((row) => getDriverAvailability(row).eligible)
                    .length
                }{" "}
                Eligible
              </Badge>
            </HStack>
          )}
        </HStack>

        {loading ? (
          <Box py={8} textAlign="center">
            <Spinner />
          </Box>
        ) : rows.length === 0 ? (
          <Box py={10} textAlign="center">
            <Text color={theme.textMuted}>No drivers found.</Text>
          </Box>
        ) : (
          <>
            {/* MOBILE / SMALL SCREEN VIEW */}
            <VStack
              display={{
                base: "flex",
                md: "none",
              }}
              align="stretch"
              spacing={3}
            >
              {rows.map((row) => {
                const availability = getDriverAvailability(row);

                return (
                  <Box
                    key={row.id}
                    p={4}
                    borderRadius="12px"
                    border={`1px solid ${theme.border}`}
                    bg={theme.surfaceAlt}
                  >
                    <HStack
                      justify="space-between"
                      align="flex-start"
                      spacing={3}
                      mb={3}
                    >
                      <Box minW={0}>
                        <Text fontWeight="800" color={theme.text} noOfLines={1}>
                          {row.name || "Unnamed driver"}
                        </Text>

                        <Text
                          fontSize="sm"
                          color={theme.textMuted}
                          textTransform="capitalize"
                        >
                          {row.vehicleType || "—"}
                        </Text>
                      </Box>

                      <VStack spacing={1} align="flex-end" flexShrink={0}>
                        <Badge
                          colorScheme={availability.online ? "green" : "gray"}
                          borderRadius="6px"
                          px={2}
                          py={0.5}
                        >
                          {availability.online ? "Online" : "Offline"}
                        </Badge>

                        <Badge
                          colorScheme={
                            availability.eligible ? "green" : "orange"
                          }
                          borderRadius="6px"
                          px={2}
                          py={0.5}
                        >
                          {availability.eligible ? "Eligible" : "Not eligible"}
                        </Badge>
                      </VStack>
                    </HStack>

                    <SimpleGrid
                      columns={{
                        base: 1,
                        sm: 2,
                      }}
                      spacing={3}
                      mb={3}
                    >
                      <Box minW={0}>
                        <Text fontSize="xs" color={theme.textMuted}>
                          Cell
                        </Text>

                        <Text
                          fontSize="sm"
                          color={theme.text}
                          wordBreak="break-word"
                        >
                          {row.phoneNumber || "—"}
                        </Text>
                      </Box>

                      <Box minW={0}>
                        <Text fontSize="xs" color={theme.textMuted}>
                          WhatsApp
                        </Text>

                        <Text
                          fontSize="sm"
                          color={theme.text}
                          wordBreak="break-word"
                        >
                          {row.whatsappNumber || "—"}
                        </Text>
                      </Box>

                      <Box minW={0}>
                        <Text fontSize="xs" color={theme.textMuted}>
                          Shift
                        </Text>

                        <Text
                          fontSize="sm"
                          color={theme.text}
                          textTransform="capitalize"
                        >
                          {String(row.currentShiftStatus || "—").replace(
                            /_/g,
                            " ",
                          )}
                        </Text>
                      </Box>

                      <Box minW={0}>
                        <Text fontSize="xs" color={theme.textMuted}>
                          Active jobs
                        </Text>

                        <Text fontSize="sm" color={theme.text}>
                          {availability.currentJobs} /{" "}
                          {availability.maxConcurrentJobs}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    {!availability.eligible && (
                      <Box
                        mb={4}
                        p={2.5}
                        borderRadius="8px"
                        bg={theme.background}
                      >
                        <Text fontSize="xs" color={theme.textMuted}>
                          {availability.reason}
                        </Text>
                      </Box>
                    )}

                    {availability.eligible && (
                      <Box
                        mb={4}
                        p={2.5}
                        borderRadius="8px"
                        bg={theme.background}
                      >
                        <Text fontSize="xs" color={theme.text} fontWeight="600">
                          Ready to receive delivery and product jobs.
                        </Text>
                      </Box>
                    )}

                    <SimpleGrid columns={2} spacing={2}>
                      <Button
                        size="sm"
                        w="100%"
                        onClick={() =>
                          updateDriverQuick(row.id, {
                            status: "active",
                          })
                        }
                      >
                        Online
                      </Button>

                      <Button
                        size="sm"
                        w="100%"
                        onClick={() =>
                          updateDriverQuick(row.id, {
                            status: "offline",
                          })
                        }
                      >
                        Offline
                      </Button>

                      <Button
                        size="sm"
                        w="100%"
                        onClick={() =>
                          updateDriverQuick(row.id, {
                            currentShiftStatus: "on_shift",
                          })
                        }
                      >
                        On Shift
                      </Button>

                      <Button
                        size="sm"
                        w="100%"
                        onClick={() =>
                          updateDriverQuick(row.id, {
                            currentShiftStatus: "off_shift",
                          })
                        }
                      >
                        Off Shift
                      </Button>

                      <Button size="sm" w="100%" onClick={() => startEdit(row)}>
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        w="100%"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => removeDriver(row.id)}
                      >
                        Delete
                      </Button>
                    </SimpleGrid>
                  </Box>
                );
              })}
            </VStack>

            {/* DESKTOP / TABLET VIEW */}
            <Box
              display={{
                base: "none",
                md: "block",
              }}
              overflowX="auto"
              sx={{
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table size="sm" minW="1180px">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Cell</Th>
                    <Th>WhatsApp</Th>
                    <Th>Vehicle</Th>
                    <Th>Online</Th>
                    <Th>Eligible</Th>
                    <Th>Shift</Th>
                    <Th>Jobs</Th>
                    <Th />
                  </Tr>
                </Thead>

                <Tbody>
                  {rows.map((row) => {
                    const availability = getDriverAvailability(row);

                    return (
                      <Tr key={row.id}>
                        <Td>
                          <Text
                            fontWeight="700"
                            color={theme.text}
                            whiteSpace="nowrap"
                          >
                            {row.name || "—"}
                          </Text>
                        </Td>

                        <Td>
                          <Text whiteSpace="nowrap">
                            {row.phoneNumber || "—"}
                          </Text>
                        </Td>

                        <Td>
                          <Text whiteSpace="nowrap">
                            {row.whatsappNumber || "—"}
                          </Text>
                        </Td>

                        <Td>
                          <Text textTransform="capitalize" whiteSpace="nowrap">
                            {row.vehicleType || "—"}
                          </Text>
                        </Td>

                        <Td>
                          <Badge
                            colorScheme={availability.online ? "green" : "gray"}
                            borderRadius="6px"
                            px={2}
                            py={0.5}
                          >
                            {availability.online ? "Online" : "Offline"}
                          </Badge>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Badge
                              colorScheme={
                                availability.eligible ? "green" : "orange"
                              }
                              borderRadius="6px"
                              px={2}
                              py={0.5}
                            >
                              {availability.eligible
                                ? "Eligible"
                                : "Not eligible"}
                            </Badge>

                            {!availability.eligible && (
                              <Text
                                fontSize="10px"
                                color={theme.textMuted}
                                whiteSpace="nowrap"
                              >
                                {availability.reason}
                              </Text>
                            )}
                          </VStack>
                        </Td>

                        <Td>
                          <Text textTransform="capitalize" whiteSpace="nowrap">
                            {String(row.currentShiftStatus || "—").replace(
                              /_/g,
                              " ",
                            )}
                          </Text>
                        </Td>

                        <Td>
                          <Text whiteSpace="nowrap">
                            {availability.currentJobs} /{" "}
                            {availability.maxConcurrentJobs}
                          </Text>
                        </Td>

                        <Td>
                          <VStack align="stretch" spacing={2}>
                            <HStack>
                              <Button
                                size="xs"
                                onClick={() =>
                                  updateDriverQuick(row.id, {
                                    status: "active",
                                  })
                                }
                              >
                                Online
                              </Button>

                              <Button
                                size="xs"
                                onClick={() =>
                                  updateDriverQuick(row.id, {
                                    status: "offline",
                                  })
                                }
                              >
                                Offline
                              </Button>
                            </HStack>

                            <HStack>
                              <Button
                                size="xs"
                                onClick={() =>
                                  updateDriverQuick(row.id, {
                                    currentShiftStatus: "on_shift",
                                  })
                                }
                              >
                                On Shift
                              </Button>

                              <Button
                                size="xs"
                                onClick={() =>
                                  updateDriverQuick(row.id, {
                                    currentShiftStatus: "off_shift",
                                  })
                                }
                              >
                                Off Shift
                              </Button>
                            </HStack>

                            <HStack>
                              <Button size="xs" onClick={() => startEdit(row)}>
                                Edit
                              </Button>

                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                onClick={() => removeDriver(row.id)}
                              >
                                Delete
                              </Button>
                            </HStack>
                          </VStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DriversPage;
